import { useEffect, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  getMarketCalendarStatus,
  getMarketCloseMinutes,
  getMarketOpenMinutes,
  getMarketTimeZone,
  type MarketCode,
  type Quote,
} from '@invest-copilot/shared';
import { fetchJSON as getJSON } from '../lib/api-client';

/**
 * 市場狀態細條：取代原本 MarketClock + MarketBriefing + Indices 三塊。
 *
 * 設計：單列（窄屏自動 wrap），刻意精簡、不占大版面、不可摺疊。
 *   兩地盤勢（市場 + 狀態 + 倒數）｜三大指數只露漲跌%（加權 / 費半 / 那指）。
 *   不再放整句 briefing 與指數絕對價（與漲跌%重複）。
 */

type StatusKind = 'open' | 'pre' | 'closed';
interface Status {
  kind: StatusKind;
  label: string;        // 盤中 / 盤前 / 已收盤 / 休市
  countdownMs: number;
  nextEvent: string;    // 距收盤 / 距開盤 / 距下次開盤
}

// 由市場時區的牆鐘時間（dateKey + 當日分鐘）反推真實 UTC 毫秒；與瀏覽器所在時區無關。
// 市場開收時間都遠離 DST 換時的凌晨，單次 offset 修正即足夠。
function tzOffsetMs(tz: string, atUtcMs: number): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(atUtcMs));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? '0');
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return asUtc - atUtcMs;
}
function zonedWallClockToMs(tz: string, dateKey: string, minutesIntoDay: number): number {
  const seg = dateKey.split('-');
  const guess = Date.UTC(Number(seg[0]), Number(seg[1]) - 1, Number(seg[2]), Math.floor(minutesIntoDay / 60), minutesIntoDay % 60);
  return guess - tzOffsetMs(tz, guess);
}
function fmtCountdown(ms: number): string {
  if (ms < 0) return '--';
  const total = Math.floor(ms / 1000);
  const d = Math.floor(total / 86_400);
  const h = Math.floor((total % 86_400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${s}s`;
}

// 盤別 / 交易日一律走 shared market-calendar（含國定假日、美股半天），
// 假日顯示「休市」、倒數自動跳過假日與週末到下一個交易日開盤。
function compute(market: MarketCode): Status {
  const now = Date.now();
  const status = getMarketCalendarStatus(market, new Date(now));
  const tz = getMarketTimeZone(market);
  const nextOpenMs = () => zonedWallClockToMs(tz, status.nextTradingDay, getMarketOpenMinutes(market)) - now;

  if (!status.isTradingDay) {
    return { kind: 'closed', label: '休市', nextEvent: '距下次開盤', countdownMs: nextOpenMs() };
  }
  if (status.session === 'pre-open') {
    return { kind: 'pre', label: '盤前', nextEvent: '距開盤', countdownMs: zonedWallClockToMs(tz, status.date, getMarketOpenMinutes(market)) - now };
  }
  if (status.session === 'regular') {
    return { kind: 'open', label: '盤中', nextEvent: '距收盤', countdownMs: zonedWallClockToMs(tz, status.date, getMarketCloseMinutes(market, status.date)) - now };
  }
  return { kind: 'closed', label: '已收盤', nextEvent: '距下次開盤', countdownMs: nextOpenMs() };
}
function computeTW(): Status { return compute('tw'); }
function computeUS(): Status { return compute('us'); }

export function MarketStatusStrip() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  void tick;

  const tw = computeTW();
  const us = computeUS();

  const quotes = useQueries({
    queries: ['^TWII', '^SOX', '^IXIC'].map((s) => ({
      queryKey: ['quote', s],
      queryFn: () => getJSON<Quote>(`/api/quote/${encodeURIComponent(s)}`),
      refetchInterval: 15_000,
      staleTime: 10_000,
      retry: 1,
    })),
  });
  const twii = quotes[0]?.data as Quote | undefined;
  const sox = quotes[1]?.data as Quote | undefined;
  const ixic = quotes[2]?.data as Quote | undefined;

  return (
    <section className="mb-2 card px-3 py-1.5 text-[11px] flex flex-wrap items-center gap-x-3 gap-y-1">
      <StatusInline market="台股" s={tw} />
      <StatusInline market="美股" s={us} />
      <span className="ml-auto flex items-center gap-x-3 gap-y-1 flex-wrap">
        <IdxInline name="加權" q={twii} />
        <IdxInline name="費半" q={sox} />
        <IdxInline name="那指" q={ixic} />
      </span>
    </section>
  );
}

function StatusInline({ market, s }: { market: string; s: Status }) {
  const dot =
    s.kind === 'open' ? 'bg-(--up)'
    : s.kind === 'pre' ? 'bg-(--accent)'
    : 'bg-(--text-muted)';
  const tone =
    s.kind === 'open' ? 'text-(--up)'
    : s.kind === 'pre' ? 'text-(--accent)'
    : 'text-(--text-muted)';
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      <span className="font-semibold text-(--text-secondary)">{market}</span>
      <span className={`font-semibold ${tone}`}>{s.label}</span>
      <span className="text-(--text-muted) tnum">{fmtCountdown(s.countdownMs)}</span>
    </span>
  );
}

function IdxInline({ name, q }: { name: string; q?: Quote }) {
  if (!q) {
    return (
      <span className="inline-flex items-center gap-1 whitespace-nowrap text-(--text-muted)">
        <span className="font-semibold">{name}</span>
        <span className="tnum">···</span>
      </span>
    );
  }
  const up = q.changePct >= 0;
  const cls = up ? 'text-(--up)' : 'text-(--down)';
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <span className="text-(--text-muted) font-semibold">{name}</span>
      <span className={`${cls} font-bold tnum`}>{up ? '▲' : '▼'}{Math.abs(q.changePct).toFixed(2)}%</span>
    </span>
  );
}
